import React, { useEffect } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import FormSection from './components/FormSection';
import FormTextField from './components/FormTextField';
import FormFieldLabel from './components/FormFieldLabel';
import FormSelect from './components/FormSelect';
import {
  CERTIFICATION_OPTIONS,
  CONSULTANT_ROLES,
  CONTRACTOR_ROLES,
  DELIVERY_METHOD_OPTIONS,
  FEE_STRUCTURE_OPTIONS,
  getProjectFacilityOptions,
  GRACE_ROLES,
  PHASE_KEYS,
  PhaseKey,
  PROJECT_SERVICE_OPTIONS,
  PROJECT_TYPE_OPTIONS,
  PROPOSAL_TYPE_OPTIONS,
} from './data';
import { InfoOutlined, Save } from '@material-ui/icons';
import { Tooltip, useMediaQuery } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { upsertProjectRequestForm } from '../../models/api/project-lifecycle';
import { useDispatch, useSelector } from 'react-redux';
import { getProjectState } from '../../features/project/selectors';
import { getDirtyOrDefinedValues } from './utils';
import { PartialProjectRequestSchema } from '../../api-client/autogenerated';
import { allowNavigation, blockNavigation } from '../../features/navigation/actions';
import _ from 'lodash';
import CircularLoader from '../loader/CircularLoader';
import { addSnackbar } from '../../features/snackbar/actions';
import { reloadProject } from '../../features/project/actions';
import { ProjectRoleFields } from './components/ProjectRoleFields';
import FormDatePicker from './components/FormDatePicker';
import Typography from '@material-ui/core/Typography';
import ProjectSchedulePhaseRow, { PHASES } from './components/ProjectSchedulePhaseRow';
import FormSectionTitle from './components/FormSectionTitle';
import FormGrid from './components/FormGrid';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import FormCheckboxes from './components/FormCheckboxes';
import {
  extraRolesSchema,
  isExtraRoleRowStarted,
  ProjectExtraRoleFields,
} from './components/ProjectExtraRoleFields';

const useStyles = makeStyles(() =>
  createStyles({
    printBreakBefore: {
      pageBreakBefore: 'always',
      '@media print': {
        paddingTop: 16,
      },
    },
    hideOnPrint: {
      '@media print': {
        display: 'none',
      },
    },
    projectDirectoryLink: {
      fontWeight: 'bold',
      color: '#0947B9',
      textDecoration: 'underline',
      cursor: 'pointer',
      '@media print': {
        display: 'none',
      },
    },
  }),
);

const projectSchedulePhaseSchema = z.object({
  enabled: z.boolean().optional(),
  label: z.string().optional(),
  start: z.string().optional(),
  completion: z.string().optional(),
  weeks: z.coerce.number().optional(),
  completionManual: z.boolean().default(false),
});

type projectSchedulePhaseType = z.infer<typeof projectSchedulePhaseSchema>;

type RolesArray = readonly { value: string; label?: string }[];

type RoleFieldSchema = z.ZodOptional<z.ZodString> | z.ZodOptional<z.ZodType<string>>;

function makeRoleSchemas<R extends RolesArray>(roles: R) {
  type V = R[number]['value'];
  type Key = `${V}Name` | `${V}Company` | `${V}Email`;

  const entries = roles.flatMap(
    (role) =>
      [
        [`${role.value}Name`, z.string().optional()],
        [`${role.value}Company`, z.string().optional()],
        [`${role.value}Email`, z.string().optional()],
      ] as const,
  );

  return Object.fromEntries(entries) as Record<Key, RoleFieldSchema>;
}

function makeRoleDefaultValues<R extends RolesArray>(roles: R, companyName: string) {
  return roles.reduce<Record<string, string>>((acc, role) => {
    const base = role.value;
    acc[`${base}Name`] = '';
    acc[`${base}Company`] = companyName;
    acc[`${base}Email`] = '';
    return acc;
  }, {});
}

const EXTRA_ROLE_FIELD_ARRAYS = [
  'graceOtherRoles',
  'contractorOtherRoles',
  'consultantOtherRoles',
] as const;

const graceRoles = makeRoleSchemas(GRACE_ROLES);
const contractorRoles = makeRoleSchemas(CONTRACTOR_ROLES);
const consultantRoles = makeRoleSchemas(CONSULTANT_ROLES);
const otherRoles = makeRoleSchemas([
  { value: 'clientProjectManager' },
  { value: 'clientInvoiceSubmission' },
] as const);

const schema = z.object({
  clientName: z.string().optional(),
  projectName: z.string().optional(),
  projectLocation: z.string().optional(),
  projectType: z.string().optional(),
  projectTypeOther: z.string().optional(),
  facilityTypes: z.array(z.string()).optional(),
  serviceTypes: z.array(z.string()).optional(),
  serviceTypesOther: z.string().optional(),
  projectNumber: z.string().optional(),

  ...graceRoles,
  graceOtherRoles: extraRolesSchema,
  ...contractorRoles,
  contractorOtherRoles: extraRolesSchema,
  ...consultantRoles,
  consultantOtherRoles: extraRolesSchema,
  ...otherRoles,

  clientNotes: z.string().optional(),

  deliveryMethod: z.string().optional(),
  deliveryMethodOther: z.string().optional(),
  proposalType: z.string().optional(),
  proposalTypeOther: z.string().optional(),
  feeStructure: z.string().optional(),
  feeStructureOther: z.string().optional(),
  projectBudget: z.string().optional(),
  constructionBudget: z.string().optional(),
  certifications: z.array(z.string()).optional(),
  certificationsOther: z.string().optional(),

  newSquareFootage: z.string().optional(),
  renovatedSquareFootage: z.string().optional(),
  totalSquareFootage: z.string().optional(),

  projectDescription: z.string().optional(),

  clientExperience: z.string().optional(),
  operationalEfficiency: z.string().optional(),
  clinicalWorkflowOptimization: z.string().optional(),
  flexibilityFutureProofing: z.string().optional(),
  sustainability: z.string().optional(),
  costReduction: z.string().optional(),
  speedToMarket: z.string().optional(),
  aestheticPlacemaking: z.string().optional(),
  security: z.string().optional(),
  codeCompliance: z.string().optional(),
  specialEquipmentIntegration: z.string().optional(),

  imaging: z.string().optional(),
  surgicalEquipment: z.string().optional(),
  labEquipment: z.string().optional(),
  foodService: z.string().optional(),
  sterileProcessing: z.string().optional(),
  specialtyMechanicalSystems: z.string().optional(),
  majorEquipmentOther: z.string().optional(),

  startDate: z.string().optional(),
  targetCompletionDate: z.string().optional(),

  schedule: z
    .object(
      Object.fromEntries(PHASES.map(({ key }) => [key, projectSchedulePhaseSchema.optional()])),
    )
    .optional(),
});

type FormValues = z.infer<typeof schema>;

const blankPhase = {
  enabled: false,
  label: '',
  start: '',
  completion: '',
  weeks: undefined,
  completionManual: false,
};

const defaultValues: Partial<FormValues> = {
  clientName: '',
  projectName: '',
  projectLocation: '',
  projectType: '',
  facilityTypes: [],
  serviceTypes: [],
  projectNumber: '',

  ...makeRoleDefaultValues(GRACE_ROLES, 'Grace Design Studios'),

  graceOtherRoles: [{ role: '', name: '', company: 'Grace Design Studios', email: '' }],
  contractorOtherRoles: [{ role: '', name: '', company: '', email: '' }],
  consultantOtherRoles: [{ role: '', name: '', company: '', email: '' }],

  clientNotes: '',

  deliveryMethod: '',
  proposalType: '',
  feeStructure: '',
  projectBudget: undefined,
  constructionBudget: undefined,
  certifications: [],

  newSquareFootage: undefined,
  renovatedSquareFootage: undefined,

  projectDescription: '',

  clientExperience: '',
  operationalEfficiency: '',
  clinicalWorkflowOptimization: '',
  flexibilityFutureProofing: '',
  sustainability: '',
  costReduction: '',
  speedToMarket: '',
  aestheticPlacemaking: '',
  security: '',
  codeCompliance: '',
  specialEquipmentIntegration: '',

  imaging: '',
  surgicalEquipment: '',
  labEquipment: '',
  foodService: '',
  sterileProcessing: '',
  specialtyMechanicalSystems: '',
  majorEquipmentOther: '',

  startDate: '',
  targetCompletionDate: '',

  schedule: {
    preDesign: { ...blankPhase },
    schematicDesign: { ...blankPhase },
    designDevelopment: { ...blankPhase },
    constructionDocuments: { ...blankPhase },
    bidNegotiation: { ...blankPhase },
    permitting: { ...blankPhase },
    constructionAdministration: { ...blankPhase },
    other1: { ...blankPhase },
    other2: { ...blankPhase },
    other3: { ...blankPhase },
    permitFilingFee: { ...blankPhase },
  },
};

const parseData = (data: PartialProjectRequestSchema): SchemaInput => {
  const parsedSchedule: Record<string, projectSchedulePhaseType> = {};

  let customLabelCount = 1;

  if (data.schedule) {
    for (const phase of data.schedule) {
      const isCustomLabel = !PHASE_KEYS.includes(phase.label as PhaseKey);
      const key = isCustomLabel ? `other${customLabelCount}` : phase.label;
      parsedSchedule[key] = {
        enabled: true,
        label: phase.label,
        start: phase.startDate,
        completion: phase.completionDate,
        completionManual: false,
        weeks: phase.weeks,
      };
      if (isCustomLabel) {
        customLabelCount++;
      }
    }
  }

  return {
    ...data,
    schedule: parsedSchedule,
  };
};

type SchemaInput = z.input<typeof schema>;
type SchemaOutput = z.output<typeof schema>;

interface Props {
  canEdit: boolean;
  isEditing: boolean;
  publicData?: PartialProjectRequestSchema | null;
}

export default function ProjectRequestForm(props: Props) {
  const { canEdit, isEditing, publicData } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const project = useSelector(getProjectState);
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { isSubmitting, dirtyFields, isDirty },
  } = useForm<SchemaInput, unknown, SchemaOutput>({
    disabled: !isEditing || !canEdit,
    resolver: zodResolver(schema),
    defaultValues: !publicData && !project?.projectRequest?.data ? defaultValues : undefined,
    reValidateMode: 'onChange',
  });

  const isMobile = useMediaQuery('(max-width: 800px)');

  const fillFormFieldsFromProject = () => {
    if (!project) return;

    if (project.architectProjectNumber) setValue('projectNumber', project.architectProjectNumber);
    setValue('projectName', project.name);
    if (project.contractAmount)
      setValue('constructionBudget', (project.contractAmount / 100).toString());
    if (project.description) setValue('projectDescription', project.description);
    if (project.bidSetup?.liveAt) {
      setValue('schedule.biddingProcurement.start', project.bidSetup.liveAt);
      setValue('schedule.biddingProcurement.enabled', true);
    }
    if (project.bidSetup?.dueDate) {
      setValue('schedule.biddingProcurement.completion', project.bidSetup.dueDate);
      setValue('schedule.biddingProcurement.enabled', true);
    }
    if (project.noticeToProceedDate) {
      setValue('schedule.constructionAdministration.start', project.noticeToProceedDate);
      setValue('schedule.constructionAdministration.enabled', true);
      setValue('startDate', project.noticeToProceedDate);
    }
    if (project.substantialCompletionDate) {
      setValue('schedule.constructionAdministration.completion', project.substantialCompletionDate);
      setValue('schedule.constructionAdministration.enabled', true);
    }
    if (project.scheduledEndDate) {
      setValue('targetCompletionDate', project.scheduledEndDate);
    }
    if (project.contractorAdministratorUser) {
      setValue('generalContractorName', project.contractorAdministratorUser.name);
      setValue('generalContractorCompany', project.contractorAdministratorUser.company?.name);
      setValue('generalContractorEmail', project.contractorAdministratorUser.email);
    }
    if (project.architectAdministratorUser) {
      setValue('projectArchitectName', project.architectAdministratorUser.name);
      setValue('projectArchitectCompany', project.architectAdministratorUser.company?.name);
      setValue('projectArchitectEmail', project.architectAdministratorUser.email);
    }
  };

  useEffect(() => {
    if (publicData) {
      const parsedData = parseData(publicData);
      reset(parsedData);
    } else if (project?.projectRequest?.data) {
      const parsedData = parseData(project.projectRequest.data);
      reset(parsedData);
    } else {
      fillFormFieldsFromProject();
    }
  }, [project, publicData]);

  useEffect(() => {
    if (isDirty) {
      dispatch(blockNavigation());
    } else {
      dispatch(allowNavigation());
    }
  }, [isDirty]);

  const projectType = watch('projectType');
  const facilityTypes = watch('facilityTypes');
  const serviceTypes = watch('serviceTypes');
  const deliveryMethod = watch('deliveryMethod');
  const proposalType = watch('proposalType');
  const feeStructure = watch('feeStructure');
  const certifications = watch('certifications');
  const newSf = watch('newSquareFootage');
  const renovatedSf = watch('renovatedSquareFootage');

  useEffect(() => {
    if (!projectType) {
      if ((facilityTypes?.length ?? 0) > 0) {
        setValue('facilityTypes', [], { shouldDirty: true, shouldValidate: true });
      }
      return;
    }

    const allowed = new Set(getProjectFacilityOptions(projectType).map((o) => o.value));
    const current = facilityTypes ?? [];
    const filtered = current.filter((v) => allowed.has(v));

    if (filtered.length !== current.length) {
      setValue('facilityTypes', filtered, { shouldDirty: true, shouldValidate: true });
    }
  }, [projectType]);

  useEffect(() => {
    if (newSf !== undefined && renovatedSf !== undefined) {
      setValue(
        'totalSquareFootage',
        ((Number(newSf) || 0) + (Number(renovatedSf) || 0)).toString(),
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
    }
  }, [newSf, renovatedSf]);

  const onSubmit = handleSubmit(
    async (data) => {
      await submit(data);
      dispatch(
        addSnackbar({ id: Date.now(), message: 'Form saved successfully', severity: 'success' }),
      );
      return;
    },
    (errors) => {
      console.log('errors', errors);
      dispatch(
        addSnackbar({
          id: Date.now(),
          message: 'Form not saved. Please complete the highlighted fields.',
          severity: 'error',
        }),
      );
    },
  );

  const submit = async (data: FormValues) => {
    if (!project) return;

    const dirtyOrDefinedValues = getDirtyOrDefinedValues<FormValues>(dirtyFields, data);
    // Blank rows are dropped; validation guarantees every remaining row is complete.
    EXTRA_ROLE_FIELD_ARRAYS.forEach((key) => {
      dirtyOrDefinedValues[key] = dirtyOrDefinedValues[key]?.filter(isExtraRoleRowStarted);
    });
    const schedule = dirtyOrDefinedValues.schedule
      ? Object.entries(dirtyOrDefinedValues.schedule)
          .filter((x) => x[1]?.enabled)
          .map(([label, value]) => ({
            label: value?.label || label,
            startDate: value?.start,
            completionDate: value?.completion,
            weeks: value?.weeks,
          }))
      : undefined;

    const body = {
      ...dirtyOrDefinedValues,
      schedule,
    };

    await upsertProjectRequestForm(project.id, body);

    reset(data);

    dispatch(allowNavigation());
    dispatch(reloadProject());

    window.scrollTo({ behavior: 'smooth', top: 0, left: 0 });
  };

  const openProjectDirectory = () => {
    window.open(
      `${window.location.origin}/main/projects/${project?.id}/directory`,
      '_blank',
      `location=yes,height=${window.innerHeight * 0.9},width=840,scrollbars=yes,status=yes`,
    );
  };

  return (
    <form noValidate onSubmit={onSubmit} style={{ marginTop: 24, paddingTop: 16 }}>
      {/* Core Project Identification */}
      <FormSection
        largeTitle
        title="A. Core Project Identification"
        // extraTitleElement={
        //   <Typography onClick={openProjectDirectory} className={classes.projectDirectoryLink}>
        //     Project Directory
        //     <OpenInNew
        //       style={{
        //         fontSize: 18,
        //         marginBottom: -3,
        //         marginLeft: 4,
        //       }}
        //     />
        //   </Typography>
        // }
        collapsible
        defaultOpen
      >
        <FormFieldLabel>Client Name:</FormFieldLabel>
        <FormTextField name="clientName" control={control} />

        <FormFieldLabel>Project Name:</FormFieldLabel>
        <FormTextField name="projectName" control={control} />

        <FormFieldLabel>Project Location (City, State):</FormFieldLabel>
        <FormTextField name="projectLocation" control={control} />

        <FormFieldLabel>Project Type:</FormFieldLabel>
        <FormSelect name="projectType" control={control} options={PROJECT_TYPE_OPTIONS} />

        {projectType === 'other' ? (
          <>
            <div />
            <FormTextField name="projectTypeOther" control={control} />
          </>
        ) : null}

        <FormFieldLabel style={{ alignSelf: 'start', marginTop: 12 }}>
          Facility Type(s):
        </FormFieldLabel>
        <FormCheckboxes
          name="facilityTypes"
          control={control}
          options={getProjectFacilityOptions(projectType)}
        />

        <div style={{ height: 16 }} />
        <div style={{ height: 16 }} />

        <FormFieldLabel style={{ alignSelf: 'start', marginTop: 12 }}>
          Service Type(s):
        </FormFieldLabel>
        <FormCheckboxes
          name="serviceTypes"
          control={control}
          options={PROJECT_SERVICE_OPTIONS}
          className={classes.printBreakBefore}
        />

        {serviceTypes?.includes('specialtyConsulting') ? (
          <>
            <FormFieldLabel>Specialty Consulting:</FormFieldLabel>
            <FormTextField name="serviceTypesOther" control={control} />
          </>
        ) : null}

        <FormFieldLabel>Grace Project Number:</FormFieldLabel>
        <FormTextField name="projectNumber" control={control} />

        <FormSectionTitle style={{ marginTop: 16 }}>Grace Design Studios Roles</FormSectionTitle>
        <div />

        {GRACE_ROLES.map((role) => (
          <ProjectRoleFields
            key={role.value}
            label={role.label}
            baseName={role.value}
            control={control}
          />
        ))}

        <ProjectExtraRoleFields
          control={control}
          name="graceOtherRoles"
          canEdit={canEdit}
          isEditing={isEditing}
          defaultCompany="Grace Design Studios"
        />

        <FormSectionTitle style={{ marginTop: 16 }}>Contractor / CM</FormSectionTitle>
        <div />

        {CONTRACTOR_ROLES.map((role) => (
          <ProjectRoleFields
            key={role.value}
            label={role.label}
            baseName={role.value}
            control={control}
          />
        ))}

        <ProjectExtraRoleFields
          control={control}
          name="contractorOtherRoles"
          canEdit={canEdit}
          isEditing={isEditing}
        />

        <FormSectionTitle style={{ marginTop: 16 }}>Consultants</FormSectionTitle>
        <div className={classes.printBreakBefore} />

        {CONSULTANT_ROLES.map((role) => (
          <ProjectRoleFields
            key={role.value}
            label={role.label}
            baseName={role.value}
            control={control}
          />
        ))}

        <ProjectExtraRoleFields
          control={control}
          name="consultantOtherRoles"
          canEdit={canEdit}
          isEditing={isEditing}
        />

        <div style={{ height: 16 }} />
        <div style={{ height: 16 }} />
        <div style={{ height: 16 }} />
        <div style={{ height: 16 }} />

        <ProjectRoleFields label="Client PM" baseName="clientProjectManager" control={control} />
        <ProjectRoleFields
          label="Client Invoice Submission"
          baseName="clientInvoiceSubmission"
          control={control}
        />

        <div style={{ height: 16 }} />
        <div style={{ height: 16 }} />
        <div style={{ height: 16 }} />
        <div style={{ height: 16 }} />

        <FormFieldLabel>Additional Client Notes:</FormFieldLabel>
        <FormTextField name="clientNotes" control={control} large />
      </FormSection>

      <FormSection
        largeTitle
        title="B. Project Commercial Data"
        collapsible
        className={classes.printBreakBefore}
      >
        <FormFieldLabel>Delivery Method:</FormFieldLabel>
        <FormSelect name="deliveryMethod" control={control} options={DELIVERY_METHOD_OPTIONS} />

        {deliveryMethod === 'other' ? (
          <>
            <div />
            <FormTextField name="deliveryMethodOther" control={control} />
          </>
        ) : null}

        <FormFieldLabel>Proposal Type:</FormFieldLabel>
        <FormSelect name="proposalType" control={control} options={PROPOSAL_TYPE_OPTIONS} />

        {proposalType === 'other' ? (
          <>
            <div />
            <FormTextField name="proposalTypeOther" control={control} />
          </>
        ) : null}

        <FormFieldLabel>Fee Structure:</FormFieldLabel>
        <FormSelect name="feeStructure" control={control} options={FEE_STRUCTURE_OPTIONS} />

        {feeStructure === 'other' ? (
          <>
            <div />
            <FormTextField name="feeStructureOther" control={control} />
          </>
        ) : null}

        <FormFieldLabel>Project Budget:</FormFieldLabel>
        <FormTextField name="projectBudget" control={control} prefix="$" signedNumericOnly />

        <FormFieldLabel>Construction Budget:</FormFieldLabel>
        <FormTextField name="constructionBudget" control={control} prefix="$" signedNumericOnly />

        <FormFieldLabel>Certifications:</FormFieldLabel>
        <FormCheckboxes name="certifications" control={control} options={CERTIFICATION_OPTIONS} />

        {certifications?.includes('other') ? (
          <>
            <div />
            <FormTextField name="certificationsOther" control={control} />
          </>
        ) : null}
      </FormSection>

      <FormSection largeTitle title="C. Project Scale & Scope" collapsible>
        <FormFieldLabel>New SF +/-</FormFieldLabel>
        <FormTextField name="newSquareFootage" control={control} signedNumericOnly />

        <FormFieldLabel>Renovated SF +/-</FormFieldLabel>
        <FormTextField name="renovatedSquareFootage" control={control} signedNumericOnly />

        <FormFieldLabel>Total SF +/-</FormFieldLabel>
        <FormTextField name="totalSquareFootage" control={control} disabled />

        <FormFieldLabel>Project Description:</FormFieldLabel>
        <FormTextField name="projectDescription" control={control} large />

        <div className={classes.printBreakBefore} />
        <div className={classes.printBreakBefore} />

        <FormSectionTitle style={{ marginTop: 16 }}>Unique Project Goals</FormSectionTitle>
        <div />

        <FormFieldLabel>Client Experience:</FormFieldLabel>
        <FormTextField name="clientExperience" control={control} large />

        <FormFieldLabel>Operational Efficiency:</FormFieldLabel>
        <FormTextField name="operationalEfficiency" control={control} large />

        <FormFieldLabel>Clinical Workflow Optimization:</FormFieldLabel>
        <FormTextField name="clinicalWorkflowOptimization" control={control} large />

        <FormFieldLabel>Flexibility / Future-Proofing:</FormFieldLabel>
        <FormTextField name="flexibilityFutureProofing" control={control} large />

        <FormFieldLabel>Sustainability:</FormFieldLabel>
        <FormTextField name="sustainability" control={control} large />

        <FormFieldLabel>Cost Reduction:</FormFieldLabel>
        <FormTextField name="costReduction" control={control} large />

        <FormFieldLabel>Speed to Market:</FormFieldLabel>
        <FormTextField name="speedToMarket" control={control} large />

        <div className={classes.printBreakBefore} />
        <div className={classes.printBreakBefore} />

        <FormFieldLabel>Aesthetic / Placemaking:</FormFieldLabel>
        <FormTextField name="aestheticPlacemaking" control={control} large />

        <FormFieldLabel>Security:</FormFieldLabel>
        <FormTextField name="security" control={control} large />

        <FormFieldLabel>Code Compliance:</FormFieldLabel>
        <FormTextField name="codeCompliance" control={control} large />

        <FormFieldLabel>Special Equipment Integration:</FormFieldLabel>
        <FormTextField name="specialEquipmentIntegration" control={control} large />

        <FormSectionTitle style={{ marginTop: 16 }}>Major Equipment Summary</FormSectionTitle>
        <div />

        <FormFieldLabel>Imaging:</FormFieldLabel>
        <FormTextField name="imaging" control={control} large />

        <FormFieldLabel>Surgical Equipment:</FormFieldLabel>
        <FormTextField name="surgicalEquipment" control={control} large />

        <FormFieldLabel>Lab Equipment:</FormFieldLabel>
        <FormTextField name="labEquipment" control={control} large />

        <div className={classes.printBreakBefore} />
        <div className={classes.printBreakBefore} />

        <FormFieldLabel>Food Service:</FormFieldLabel>
        <FormTextField name="foodService" control={control} large />

        <FormFieldLabel>Sterile Processing:</FormFieldLabel>
        <FormTextField name="sterileProcessing" control={control} large />

        <FormFieldLabel>Specialty Mechanical Systems:</FormFieldLabel>
        <FormTextField name="specialtyMechanicalSystems" control={control} large />

        <FormFieldLabel>Other:</FormFieldLabel>
        <FormTextField name="majorEquipmentOther" control={control} large />
      </FormSection>

      <FormSection largeTitle title="D. Schedule" noGrid collapsible>
        <FormGrid style={{ marginBottom: 32 }}>
          <FormFieldLabel>Project Start Date:</FormFieldLabel>
          <FormDatePicker name="startDate" control={control} />

          <FormFieldLabel>Target Completion Date:</FormFieldLabel>
          <FormDatePicker name="targetCompletionDate" control={control} />
        </FormGrid>

        <div className={classes.printBreakBefore} />

        {!isMobile ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1.5fr 0.2fr 1fr 1fr 0.4fr',
              columnGap: '12px',
              marginBottom: 8,
              fontWeight: 600,
            }}
          >
            <Typography>Select relevant phase(s):</Typography>
            <div />
            <Typography>Start</Typography>
            <Typography style={{ display: 'inline-flex', alignItems: 'center' }}>
              Completion Date
              <Tooltip
                arrow
                placement="top"
                title="If you would prefer to manually enter Completion Date, you can override that cell and input whatever date you would like. Weeks will not autopopulate."
              >
                <InfoOutlined fontSize="small" style={{ marginLeft: 4 }} />
              </Tooltip>
            </Typography>
            <Typography style={{ display: 'inline-flex', alignItems: 'center' }}>
              Weeks
              <Tooltip
                arrow
                placement="top"
                title="If you enter Start Date and Weeks, the Completion Date will autopopulate."
              >
                <InfoOutlined fontSize="small" style={{ marginLeft: 4 }} />
              </Tooltip>
            </Typography>
          </div>
        ) : null}

        {PHASES.map((p) => (
          <ProjectSchedulePhaseRow
            key={p.key}
            control={control}
            setValue={(...args) => (args as any, args[2] as any, setValue as any)(...args)}
            phaseKey={p.key}
            label={p.label}
            allowCustomLabel={p.allowCustomLabel}
          />
        ))}
      </FormSection>

      {isEditing ? (
        <div
          className={classes.hideOnPrint}
          style={{
            position: isMobile ? 'static' : 'sticky',
            bottom: 0,
            width: 'calc(100% + 32px)',
            padding: 16,
            marginLeft: -16,
            backgroundColor: '#FFF',
            borderTop: '1px solid #CCC',
            zIndex: 100,
          }}
        >
          {!isSubmitting ? (
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!canEdit}
              startIcon={<Save />}
            >
              Save Changes
            </Button>
          ) : (
            <CircularLoader style={{ justifyContent: 'start', paddingLeft: 32 }} />
          )}
        </div>
      ) : null}
    </form>
  );
}
